import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { BlockUserButton } from "@/components/BlockUserButton";
import { ReportOffPlatformButton } from "@/components/ReportOffPlatformButton";
import { Send, Ban, ShieldQuestion, Trash2, ShieldCheck, MoreVertical, ImagePlus, Film, X, Loader2 } from "lucide-react";
import { timeAgo, listingPath } from "@/lib/constants";
import { toast } from "@/lib/toast";
import { convTheme, convLabels, resolveConvType } from "@/lib/conversationTheme";
import { FoundingSeatChip } from "@/components/FoundingSeatChip";
import { VouchMark } from "@/components/VouchMark";
import { ConvTypePill } from "@/components/conversation/ConversationChrome";

// The buyer<->seller thread pane. Extracted from the old /messages so the unified inbox can host
// MARKETPLACE alongside the staff scopes. Keeps every /messages affordance: listing/order context
// header, Request support, Report, Block, retention notice.
export default function MarketplaceThread({ conversation, onHidden, onActivity }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const conversationId = conversation?.conversation_id;
  const [messages, setMessages] = useState([]);
  const [canReply, setCanReply] = useState(true);
  const [blocked, setBlocked] = useState(false);
  const [supportActive, setSupportActive] = useState(false);
  const [supportRequested, setSupportRequested] = useState(false);
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState([]); // staged, not yet sent: {url, thumb_url, width, height}
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null); // full-size url currently open
  const [video, setVideo] = useState(null); // staged clip, not yet sent: {url, duration}
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const fileRef = useRef();
  const videoRef = useRef();
  const bottomRef = useRef();
  const listRef = useRef();
  // Narrow screens collapse the header action row (support/report/block/hide) into a ⋯ menu so it
  // can't overflow and overlap the conversation title.
  const [isNarrow, setIsNarrow] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setIsNarrow(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  const convType = conversation ? resolveConvType(conversation.other_user) : "member";
  const theme = convTheme(convType);
  const labels = convLabels(convType, { audience: "member", context: conversation?.other_user?.context || "seller" });

  const loadMessages = useCallback(async (silent) => {
    if (!conversationId) return;
    try {
      const { data } = await api.get(`/conversations/${conversationId}/messages`);
      setMessages(data.messages);
      setCanReply(data.can_reply !== false);
      setBlocked(!!data.blocked);
      setSupportActive(!!data.support_active);
      setSupportRequested(!!data.support_requested);
      onActivity?.();
      // Scroll only this thread's list (see StaffChatPane) — never scrollIntoView, which jumps
      // the whole page to the bottom on Enter-to-send.
      if (!silent) setTimeout(() => { const el = listRef.current; if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" }); }, 50);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  useEffect(() => { loadMessages(false); }, [loadMessages]);
  useEffect(() => {
    const t = setInterval(() => loadMessages(true), 5000);
    return () => clearInterval(t);
  }, [loadMessages]);

  const MAX_ATTACH = 6;
  const pickFiles = () => fileRef.current?.click();
  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []).filter(
      (f) => (f.type || "").startsWith("image/") || /\.(jpe?g|png|webp|heic|heif|gif|bmp)$/i.test(f.name)
    );
    if (e.target) e.target.value = "";
    if (!files.length) return;
    if (attachments.length + files.length > MAX_ATTACH) {
      toast.error(`Up to ${MAX_ATTACH} photos per message.`);
      return;
    }
    setUploading(true);
    try {
      for (const f of files) {
        if (f.size > 10 * 1024 * 1024) { toast.error(`"${f.name}" is over 10MB.`); continue; }
        const fd = new FormData();
        fd.append("file", f);
        try {
          const { data } = await api.post("/messages/upload-attachment", fd);
          setAttachments((prev) => [...prev, data]);
        } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Upload failed."); }
      }
    } finally { setUploading(false); }
  };
  const removeAttachment = (i) => setAttachments((prev) => prev.filter((_, idx) => idx !== i));

  const MAX_VIDEO_MB = 50;
  const pickVideo = () => videoRef.current?.click();
  const onVideo = async (e) => {
    const f = (e.target.files || [])[0];
    if (e.target) e.target.value = "";
    if (!f) return;
    if (!(f.type || "").startsWith("video/") && !/\.(mp4|mov|webm|m4v|mkv|3gp)$/i.test(f.name)) {
      toast.error("Choose a video file (MP4, MOV, WebM, M4V, MKV or 3GP)."); return;
    }
    if (f.size > MAX_VIDEO_MB * 1024 * 1024) { toast.error(`Video is over ${MAX_VIDEO_MB}MB.`); return; }
    setUploadingVideo(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/messages/upload-video", fd);
      setVideo(data); // {url, duration}
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Video upload failed."); }
    finally { setUploadingVideo(false); }
  };

  const send = async (e) => {
    e.preventDefault();
    if (uploading || uploadingVideo) return;
    const body = text.trim();
    if ((!body && attachments.length === 0 && !video) || !conversation) return;
    try {
      await api.post("/messages", {
        listing_id: conversation.listing_id,
        recipient_id: conversation.other_user.user_id,
        text: body,
        attachments,
        video: video?.url || null,
      });
      setText("");
      setAttachments([]);
      setVideo(null);
      loadMessages(false);
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Couldn't send message."); }
  };

  const requestSupport = async () => {
    try {
      await api.post(`/conversations/${conversationId}/request-support`);
      setSupportRequested(true);
      toast.success("OpticSwap Support has been notified. They may review this conversation to help resolve it.");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const hideConversation = async () => {
    if (!window.confirm("Hide this conversation from your inbox? It stays on record and reappears if a new message arrives.")) return;
    try {
      await api.delete(`/conversations/${conversationId}`);
      toast.success("Conversation hidden.");
      setMessages([]);
      onHidden?.();
      navigate("/inbox", { replace: true });
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  if (!conversation) {
    return <div className="flex-1 flex items-center justify-center text-muted-foreground font-mono uppercase text-sm">Select a conversation</div>;
  }

  return (
    <div className="flex flex-col h-full" style={{ background: theme.windowBg }} data-testid="marketplace-thread">
      <div className="px-3 py-2.5 flex items-center justify-between gap-2" style={{ background: theme.headerBg }}>
        <div className="min-w-0 flex items-center gap-2.5">
          <ConvTypePill theme={theme} label={labels.pill} testid="conv-type-pill" />
          <div className="min-w-0">
            <span className="font-heading text-lg leading-tight flex items-center truncate" style={{ color: theme.headerText }}>
              <span className="flex items-center gap-2 min-w-0 truncate">
                <span className="truncate">@{conversation.other_user.username}</span>
                <FoundingSeatChip seat={conversation.other_user.founding_number} accountType={conversation.other_user.account_type} testid="conv-founding" />
              </span>
              {conversation.other_user.vouch?.invited_by_username && (
                <VouchMark inviter={conversation.other_user.vouch.invited_by_username} size={16} testid="conv-vouch-mark" />
              )}
            </span>
            {conversation.listing_available === false ? (
              <span className="block text-xs font-mono uppercase truncate" data-testid="conv-listing-unavailable"
                style={{ color: theme.headerText, opacity: 0.55 }}>Listing no longer available</span>
            ) : (
              <Link to={listingPath(conversation)} className="block text-xs font-mono uppercase truncate hover:underline" style={{ color: theme.headerText, opacity: 0.8 }}>{conversation.listing_title}</Link>
            )}
          </div>
        </div>        <div className="flex items-center gap-2 shrink-0">
          {(() => {
            const actionButtons = (
              <>
                <button onClick={requestSupport} disabled={supportRequested || supportActive} data-testid="request-support-btn"
                  title="Ask OpticSwap Support to review this conversation"
                  className="inline-flex items-center gap-1.5 border disabled:opacity-50 disabled:cursor-not-allowed font-mono uppercase text-xs px-3 py-2 transition-colors"
                  style={{ borderColor: theme.pill.border, color: theme.headerText }}>
                  <ShieldQuestion className="w-3.5 h-3.5" /> {supportActive ? "Support here" : supportRequested ? "Support requested" : "Request support"}
                </button>
                <ReportOffPlatformButton username={conversation.other_user.username} />
                <BlockUserButton username={conversation.other_user.username} blocked={blocked} onChanged={() => loadMessages(true)} />
                <button onClick={hideConversation} data-testid="hide-conversation-btn" title="Hide from my inbox"
                  className="inline-flex items-center gap-1.5 border font-mono uppercase text-xs px-2.5 py-2 transition-colors"
                  style={{ borderColor: theme.pill.border, color: theme.headerText }}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </>
            );
            if (!isNarrow) return actionButtons;
            return (
              <div className="relative">
                <button onClick={() => setMenuOpen((o) => !o)} data-testid="thread-actions-menu-btn"
                  title="Conversation actions"
                  className="inline-flex items-center justify-center border p-2 transition-colors"
                  style={{ borderColor: theme.pill.border, color: theme.headerText }}>
                  <MoreVertical className="w-4 h-4" />
                </button>
                {menuOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                    <div data-testid="thread-actions-menu" onClick={() => setMenuOpen(false)}
                      className="absolute right-0 top-full mt-1 z-20 bg-surface border border-sniper flex flex-col items-stretch gap-1 p-1.5 min-w-[200px] shadow-xl">
                      {actionButtons}
                    </div>
                  </>
                )}
              </div>
            );
          })()}
        </div>
      </div>
      {supportActive && (
        <div className="px-3 py-2 border-b border-sniper bg-olive/10 flex items-center gap-2" data-testid="support-active-banner">
          <ShieldCheck className="w-4 h-4 text-olive-bright shrink-0" />
          <span className="font-mono text-[11px] uppercase text-olive-bright">OpticSwap Support is in this conversation to help resolve a case.</span>
        </div>
      )}
      <div ref={listRef} className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-4 space-y-3" data-testid="message-thread">
        {messages.map((m, idx) => {
          // Bulletproof key: legacy/edge data could carry a missing or duplicate message_id, and a
          // duplicate React key silently drops rows from the DOM. The array index guarantees uniqueness
          // (the list is append-only, sorted by created_at) so EVERY message always renders.
          const key = `${m.message_id || "m"}-${idx}`;
          // System notices (e.g. "New offer — $X") are stored with `is_system` (some legacy rows use
          // `system`); render either as a centered chip rather than a normal chat bubble.
          if (m.is_system || m.system) {
            return (
              <div key={key} className="flex justify-center" data-testid={`msg-system-${m.message_id}`}>
                <span className="font-mono text-[10px] uppercase tracking-wide text-teal-300 border border-teal-300/30 bg-teal-300/5 px-2 py-1">
                  <ShieldCheck className="w-3 h-3 inline mr-1" />{m.text} · {timeAgo(m.created_at)}
                </span>
              </div>
            );
          }
          if (m.sender_role === "admin_support") {
            return (
              <div key={key} className="flex justify-start" data-testid={`msg-support-${m.message_id}`}>
                <div className="max-w-[80%] px-3 py-2 text-sm font-body bg-olive/10 border border-olive-bright/50 text-secondary-foreground">
                  <span className="font-mono text-[9px] uppercase tracking-wider text-olive-bright border border-olive-bright/50 px-1">OpticSwap Support</span>
                  <p className="mt-1 text-foreground whitespace-pre-wrap break-words">{m.text}</p>
                  <span className="block text-[10px] font-mono opacity-60 mt-1">{timeAgo(m.created_at)}</span>
                </div>
              </div>
            );
          }
          const mine = m.sender_id === user?.user_id;
          return (
            <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className="max-w-[75%] px-3 py-2 text-sm font-body"
                style={mine
                  ? { background: theme.sendBg, border: `1px solid ${theme.sendBorder}`, color: theme.sendText }
                  : { background: `rgba(${theme.rgb},0.12)`, border: `1px solid ${theme.subheaderBorder}`, color: theme.body }}>
                {m.text ? <p className="whitespace-pre-wrap break-words">{m.text}</p> : null}
                {m.attachments?.length > 0 && (
                  <div className={`grid gap-1.5 ${m.attachments.length > 1 ? "grid-cols-2" : "grid-cols-1"} ${m.text ? "mt-2" : ""}`} data-testid={`message-attachments-${m.message_id}`}>
                    {m.attachments.map((a, i) => (
                      <button key={i} type="button" onClick={() => setLightbox(a.url)}
                        className="block overflow-hidden border border-black/25 bg-black/20 hover:opacity-90 transition-opacity"
                        data-testid={`message-attachment-${m.message_id}-${i}`}>
                        <img src={a.thumb_url || a.url} alt="Shared photo" loading="lazy"
                          className="w-full h-full max-h-56 object-cover" />
                      </button>
                    ))}
                  </div>
                )}
                {m.video && (
                  <div className={`${m.text || m.attachments?.length ? "mt-2" : ""}`} data-testid={`message-video-${m.message_id}`}>
                    <video src={m.video} controls playsInline preload="metadata"
                      className="w-full max-h-72 rounded-sm border border-black/25 bg-black" />
                  </div>
                )}
                <span className="block text-[10px] font-mono opacity-60 mt-1">{timeAgo(m.created_at)}</span>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      {canReply ? (
        <form onSubmit={send} className="p-3" style={{ background: theme.composerBg, borderTop: `1px solid ${theme.composerBorder}` }}>
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2" data-testid="message-staged-attachments">
              {attachments.map((a, i) => (
                <div key={i} className="relative w-16 h-16 border border-sniper overflow-hidden" data-testid={`message-staged-${i}`}>
                  <img src={a.thumb_url || a.url} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => removeAttachment(i)} data-testid={`message-staged-remove-${i}`}
                    title="Remove" className="absolute top-0 right-0 bg-black/70 hover:bg-black/90 text-white p-0.5 transition-colors">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
              {uploading && <div className="w-16 h-16 border border-dashed border-sniper flex items-center justify-center"><Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /></div>}
            </div>
          )}
          {attachments.length === 0 && uploading && (
            <div className="mb-2 flex items-center gap-2 font-mono text-[10px] text-muted-foreground" data-testid="message-attach-uploading"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading photo…</div>
          )}
          {video && (
            <div className="mb-2 relative inline-block" data-testid="message-staged-video">
              <video src={video.url} controls playsInline preload="metadata"
                className="max-h-32 rounded-sm border border-sniper bg-black" />
              <button type="button" onClick={() => setVideo(null)} data-testid="message-staged-video-remove"
                title="Remove video" className="absolute top-0 right-0 bg-black/70 hover:bg-black/90 text-white p-0.5 transition-colors">
                <X className="w-3 h-3" />
              </button>
              {video.duration != null && <span className="absolute bottom-1 left-1 bg-black/70 text-white font-mono text-[9px] px-1 rounded-sm">{Math.round(video.duration)}s</span>}
            </div>
          )}
          {uploadingVideo && (
            <div className="mb-2 flex items-center gap-2 font-mono text-[10px] text-muted-foreground" data-testid="message-video-uploading"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading video…</div>
          )}
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid="message-attach-input" />
            <input ref={videoRef} type="file" accept="video/*" className="hidden" onChange={onVideo} data-testid="message-video-input" />
            <button type="button" onClick={pickFiles} disabled={uploading || attachments.length >= MAX_ATTACH} data-testid="message-attach-btn"
              title="Attach photos" className="px-3 flex items-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: theme.inputBg, border: `1px solid ${theme.inputBorder}`, color: theme.body }}>
              <ImagePlus className="w-4 h-4" />
            </button>
            <button type="button" onClick={pickVideo} disabled={uploadingVideo || !!video} data-testid="message-video-btn"
              title="Attach a short video (max 60s)" className="px-3 flex items-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: theme.inputBg, border: `1px solid ${theme.inputBorder}`, color: theme.body }}>
              <Film className="w-4 h-4" />
            </button>
            <input value={text} onChange={(e) => setText(e.target.value)} data-testid="message-thread-input"
              placeholder="Type a message..." className="flex-1 outline-none px-3 py-2.5 transition-colors"
              style={{ background: theme.inputBg, border: `1px solid ${theme.inputBorder}`, color: theme.body }} />
            <button type="submit" disabled={uploading || uploadingVideo || (!text.trim() && attachments.length === 0 && !video)} data-testid="message-thread-send"
              className="px-4 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: theme.sendBg, border: `1px solid ${theme.sendBorder}`, color: theme.sendText }}><Send className="w-4 h-4" /></button>
          </div>
        </form>
      ) : (
        <div className="p-3 border-t border-sniper flex items-center gap-2 text-muted-foreground" data-testid="message-thread-readonly">
          <Ban className="w-4 h-4 text-blaze shrink-0" />
          <span className="font-mono text-xs uppercase">This conversation is blocked — you can't send new messages.</span>
        </div>
      )}
      {lightbox && (
        <div className="fixed inset-0 z-[90] bg-black/90 flex items-center justify-center p-4" onClick={() => setLightbox(null)} data-testid="message-lightbox">
          <button onClick={() => setLightbox(null)} data-testid="message-lightbox-close"
            className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors"><X className="w-7 h-7" /></button>
          <img src={lightbox} alt="Shared photo, full size" onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full object-contain" data-testid="message-lightbox-img" />
        </div>
      )}
    </div>
  );
}
